/**
 * useTheme Hook
 * 
 * Provides theme state and toggle functionality for the entire app.
 * Works in conjunction with ThemeAndFavicon component for initial setup.
 */
"use client";

import { useEffect, useState } from 'react';

export function useTheme(logoLightUrl?: string, logoDarkUrl?: string) {
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Sync with initial theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light');
    setIsDarkMode(initialTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = isDarkMode ? 'light' : 'dark';
    setIsDarkMode(!isDarkMode);

    // Update document class
    if (newTheme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }

    // Save preference
    localStorage.setItem('theme', newTheme);

    // Update favicon if logo URLs provided
    if (logoLightUrl && logoDarkUrl) {
      const favicon = document.getElementById('site-favicon') as HTMLLinkElement | null;
      if (favicon) {
        favicon.href = newTheme === 'light' ? logoLightUrl : logoDarkUrl;
      }
    }
  };

  return { isDarkMode, toggleTheme };
}
